import { assertValue } from '@shared/helpers'
import { AppName, ConfigResponse } from '@shared/types'
import { getConfig } from '@ui-common/api/endpoints'
import { debugLog } from '@ui-common/utils/helpers'
import { redirectToMaintenancePageIfNeeded } from '@ui-common/utils/statusCheck'
import { assign, fromPromise, setup } from 'xstate'

import { InitializeBaseContext } from './types'

export interface InitializeActions {
  onInitializedExit: () => void
  onInitialized: (data?: ConfigResponse<string>) => void
  getAndStoreInputParams: () => InputParams
}

export type InputParams = { flow: string; sourceChannel: string }

export type InitializeStepContext = ConfigResponse<string> & Partial<InputParams> & InitializeBaseContext

export type InitializeStepEvent = { type: 'EXIT' }

export const GET_AND_STORE_INPUT_PARAMS = 'getAndStoreInputParams'
export const ENSURE_BFF_AVAILABILITY = 'ensureBffAvailability'
export const GET_CONFIG = 'getConfig'
export const ERROR = 'error'

export const initializeStepMachine = setup({
  types: {
    context: {} as InitializeStepContext,
    events: {} as InitializeStepEvent,
    input: {} as InitializeBaseContext,
  },
  actors: {
    getAndStoreInputParams: fromPromise(async ({ input }: { input: { getAndStoreInputParams: () => InputParams } }) => {
      debugLog('Actor: getAndStoreInputParams')
      return input.getAndStoreInputParams()
    }),
    ensureBffAvailability: fromPromise(async ({ input }: { input: { appName: AppName } }) => {
      debugLog('Actor: ensureBffAvailability')
      await redirectToMaintenancePageIfNeeded(import.meta.env.VITE_ENV_ID, input.appName)
    }),
    getConfig: fromPromise(async ({ input }: { input: InputParams }) => {
      debugLog('Actor: getConfig')
      return getConfig(input.sourceChannel, input.flow)
    }),
  },
  actions: {
    exit: ({ context }) => {
      context.actions.onInitializedExit()
    },
    proceedToNextStep: ({ context }) => {
      if (!!context.exitUrls && !!context.params) {
        context.actions.onInitialized({
          exitUrls: context.exitUrls,
          params: context.params,
        })
      } else {
        context.actions.onInitialized()
      }
    },
    storeInputParams: assign((_, inputParams: InputParams) => {
      debugLog('Action: storeInputParams')
      return inputParams
    }),
    storeConfig: assign((_, config: ConfigResponse<string>) => {
      debugLog('Action: storeConfig')
      return config
    }),
  },
  guards: {
    shouldGetConfig: ({ context }) => {
      return !!context.shouldGetConfig
    },
  },
}).createMachine({
  id: 'initializeStepMachine',
  initial: GET_AND_STORE_INPUT_PARAMS,
  context: ({ input }) => input,
  states: {
    [GET_AND_STORE_INPUT_PARAMS]: {
      invoke: {
        src: 'getAndStoreInputParams',
        input: ({ context }) => ({
          getAndStoreInputParams: context.actions.getAndStoreInputParams,
        }),
        onDone: {
          actions: [{ type: 'storeInputParams', params: ({ event }) => event.output }],
          target: ENSURE_BFF_AVAILABILITY,
        },
      },
    },
    [ENSURE_BFF_AVAILABILITY]: {
      invoke: {
        src: 'ensureBffAvailability',
        input: ({ context }) => ({
          appName: context.appName,
        }),
        onDone: [
          {
            guard: { type: 'shouldGetConfig' },
            target: GET_CONFIG,
          },
          {
            actions: [{ type: 'proceedToNextStep' }],
          },
        ],
        onError: {
          target: ERROR,
        },
      },
    },
    [GET_CONFIG]: {
      invoke: {
        input: ({ context }) => {
          return {
            flow: assertValue(context.flow, 'context.flow'),
            sourceChannel: assertValue(context.sourceChannel, 'context.sourceChannel'),
          }
        },
        src: 'getConfig',
        onDone: {
          actions: [{ type: 'storeConfig', params: ({ event }) => event.output }, { type: 'proceedToNextStep' }],
        },
        onError: {
          target: ERROR,
        },
      },
    },
    [ERROR]: {
      on: {
        EXIT: { actions: { type: 'exit' } },
      },
    },
  },
})
